<script setup lang="ts">
import { computed, defineProps, defineEmits, ref } from "vue";
import MonthTableFiltersComponent from "@/views/Calculators/PersonalDates/components/monthReport/MonthTableFiltersComponent.vue";
import {
  BestDaysByCategory,
  FavorableMonthFilter,
  MonthDayReport,
} from "@/types/calculators";
import { useI18n } from "vue-i18n";
import EmptyDayCardComponent from "@/views/Calculators/PersonalDates/components/monthReport/EmptyDayCardComponent.vue";
import DayCardComponent from "@/views/Calculators/PersonalDates/components/monthReport/DayCardComponent.vue";
import { getWeekdayName } from "@/utils/date";
import { useLangStore } from "@/stores/langStore";
import { BADGE_TIER_CLASSES } from "@/constants/tongShu";

const props = defineProps<{
  bestDaysByCategory: BestDaysByCategory;
  days: MonthDayReport[];
  selectedCard: MonthDayReport | null;
  isCalendarView: boolean;
}>();

const emits = defineEmits<{
  (e: "selectCard", selectedCard: MonthDayReport, isForceOpen?: boolean): void;
}>();

const { t } = useI18n();
const langStore = useLangStore();

const selectedDates = ref<string[] | null>(null);
const goldenDays = computed(() => {
  return props.days.filter((item) => item.tier === "golden");
});
const emptyCardsCount = computed(() => {
  return props.days[0].day_of_week_num - 1;
});

function handleSelectedFilter(filter: FavorableMonthFilter) {
  if (filter === "allDays") {
    selectedDates.value = null;
    return;
  }
  const filterSourceMap: Record<
    Exclude<FavorableMonthFilter, "allDays">,
    { date: string }[]
  > = {
    goldenDays: goldenDays.value,
    commerceDays: props.bestDaysByCategory.commerce.dates,
    marriageDays: props.bestDaysByCategory.marriage.dates,
    livingMoveDays: props.bestDaysByCategory.living_move.dates,
    careerDays: props.bestDaysByCategory.career.dates,
    travelDays: props.bestDaysByCategory.travel.dates,
    medicalDays: props.bestDaysByCategory.medical.dates,
  };
  const targetArray = filterSourceMap[filter];

  selectedDates.value = targetArray.map((item) => item.date);
}

function formatHourRange(rangeStr: string): string {
  if (!rangeStr) return "";

  const startTime = rangeStr.split("-")[0].trim();
  return `(${startTime})`;
}

function getBestHoursString(dayReport: MonthDayReport): string {
  const bestHours = dayReport.best_hours || [];

  return bestHours
    .map((h) => `${h.branch}时 ${formatHourRange(h.time_std)}`)
    .join(", ");
}
</script>

<template>
  <div
    class="bg-white rounded-2xl border border-divider p-4 sm:p-6 shadow-sm space-y-5"
  >
    <MonthTableFiltersComponent
      :allDays="days.length"
      :goldenDays="goldenDays.length"
      :commerceDays="bestDaysByCategory.commerce.dates.length"
      :marriageDays="bestDaysByCategory.marriage.dates.length"
      :livingMoveDays="bestDaysByCategory.living_move.dates.length"
      :careerDays="bestDaysByCategory.career.dates.length"
      :travelDays="bestDaysByCategory.travel.dates.length"
      :medicalDays="bestDaysByCategory.medical.dates.length"
      @selectFilter="handleSelectedFilter"
    />
    <div class="space-y-3" v-if="isCalendarView">
      <!-- WEEK ROW -->
      <div
        class="grid grid-cols-7 gap-1 sm:gap-2 text-center text-[10px] sm:text-xs font-bold uppercase text-muted py-2 bg-secondary rounded-xl border border-divider"
      >
        <div>{{ t("week-names.monday") }}</div>
        <div>{{ t("week-names.tuesday") }}</div>
        <div>{{ t("week-names.wednesday") }}</div>
        <div>{{ t("week-names.thursday") }}</div>
        <div>{{ t("week-names.friday") }}</div>
        <div class="text-active">{{ t("week-names.saturday") }}</div>
        <div class="text-active">{{ t("week-names.sunday") }}</div>
      </div>
      <!-- DAYS GRID -->
      <div class="grid grid-cols-7 gap-1 sm:gap-2.5">
        <EmptyDayCardComponent v-for="index in emptyCardsCount" :key="index" />
        <DayCardComponent
          v-for="(dayReport, index) in days"
          :key="dayReport.date"
          :dayReport="dayReport"
          :cardNumber="index + 1"
          :isCardSelected="
            selectedCard !== null && selectedCard.date === dayReport.date
          "
          :isCardMuted="
            selectedDates !== null && !selectedDates.includes(dayReport.date)
          "
          @click="emits('selectCard', dayReport)"
        />
      </div>
    </div>
    <div v-else>
      <div class="grid grid-cols-1 gap-3 md:hidden">
        <div
          class="p-3.5 rounded-xl border transition-all cursor-pointer space-y-2 bg-[#FFFDF7] border-gold/60"
          v-for="dayReport in days"
          :key="dayReport.date"
          @click="emits('selectCard', dayReport, true)"
        >
          <div class="flex items-center justify-between">
            <div class="font-bold text-sm text-brown">
              {{ dayReport.date.split("-").reverse().join(".") }} ({{
                getWeekdayName(dayReport.date, langStore.current, "short")
              }}) •
              <span class="font-caudex text-base">
                {{ dayReport.pillars.day }}
              </span>
            </div>
            <span
              class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gold text-white"
              :class="BADGE_TIER_CLASSES[dayReport.tier]"
            >
              {{ t(`calculators.personal-date.card-barge.${dayReport.tier}`) }}
              ({{ dayReport.combined_index }}%)
            </span>
          </div>
          <div
            class="flex items-center justify-between text-xs text-alternative-gray"
          >
            <span>
              {{
                t("calculators.personal-date.card-barge.month-table.officer")
              }}
              <strong class="text-brown">
                {{ dayReport.officer_12.name }}
                ({{ dayReport.officer_12.title_ua }})
              </strong>
            </span>
            <span>
              {{ t("calculators.personal-date.top-days.rang") }}:
              <strong>
                {{ dayReport.general_status.rank_code }}
              </strong>
            </span>
          </div>
          <div
            class="text-xs text-success font-medium truncate"
            v-if="dayReport.yi_preview.length > 0"
          >
            宜: {{ dayReport.yi_preview.slice(0, 3).join(", ") }}
          </div>
          <div
            class="text-[11px] text-active font-bold flex items-center justify-between pt-1 border-t border-divider/60"
          >
            <span>
              {{ t("calculators.personal-date.month-table.hours") }}
              {{ getBestHoursString(dayReport) }}
            </span>
            <span>
              {{ t("calculators.personal-date.month-table.openDay") }} →
            </span>
          </div>
        </div>
      </div>

      <div class="hidden md:block overflow-x-auto">
        <table class="w-full text-left text-xs border-collapse">
          <thead>
            <tr
              class="border-b border-divider bg-secondary text-muted uppercase text-[11px]"
            >
              <th class="py-3 px-3">
                {{ $t("calculators.personal-date.month-table.columns.date") }}
              </th>
              <th class="py-3 px-3">
                {{
                  $t(
                    "calculators.personal-date.month-table.columns.pillarManager"
                  )
                }}
              </th>
              <th class="py-3 px-3">
                {{
                  $t(
                    "calculators.personal-date.month-table.columns.generalRank"
                  )
                }}
              </th>
              <th class="py-3 px-3">
                {{
                  $t(
                    "calculators.personal-date.month-table.columns.baziResonance"
                  )
                }}
              </th>
              <th class="py-3 px-3">
                {{
                  $t(
                    "calculators.personal-date.month-table.columns.recommendedActivities"
                  )
                }}
              </th>
              <th class="py-3 px-3">
                {{
                  $t("calculators.personal-date.month-table.columns.bestHours")
                }}
              </th>
              <th class="py-3 px-3 text-right">
                {{ $t("calculators.personal-date.month-table.columns.action") }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-divider">
            <tr
              class="hover:bg-hover transition-colors cursor-pointer"
              :class="dayReport.tier === 'golden' ? 'bg-[#FFFDF9]' : ''"
              v-for="dayReport in days"
              :key="dayReport.date"
              @click="emits('selectCard', dayReport, true)"
            >
              <td class="py-3 px-3">
                <div class="font-bold text-brown">
                  {{ dayReport.date.split("-").reverse().join(".") }} ({{
                    getWeekdayName(dayReport.date, langStore.current, "short")
                  }})
                </div>
                <div class="text-[11px] text-muted">
                  {{ $t("calculators.personal-date.month-table.monthly") }}
                  {{ dayReport.lunar_short }}
                </div>
              </td>
              <td class="py-3 px-3">
                <div class="font-bold text-sm font-caudex text-brown">
                  {{ dayReport.pillars.day }} ({{ dayReport.day_animal }})
                </div>
                <div class="text-[11px] text-muted">
                  {{ dayReport.officer_12.name }} -
                  {{ dayReport.officer_12.title_ua }}
                </div>
              </td>
              <td class="py-3 px-3">
                <span
                  class="px-2 py-0.5 rounded text-[11px] font-bold bg-secondary text-brown"
                >
                  {{ $t("calculators.personal-date.top-days.rang") }}
                  {{ dayReport.general_status.rank_code }}
                </span>
              </td>
              <td class="py-3 px-3">
                <div class="font-bold text-brown">
                  {{ dayReport.personal_status.score }}% ({{
                    dayReport.personal_status.ten_god_stem
                  }})
                </div>
                <div
                  v-if="dayReport.personal_status.structures.length > 0"
                  class="text-[11px] text-gold font-medium max-w-[220px] truncate"
                >
                  {{ dayReport.personal_status.structures[0] }}
                </div>
                <div
                  v-if="dayReport.personal_status.stars.length > 0"
                  class="text-[11px] text-gold font-medium max-w-[220px] truncate"
                >
                  {{ dayReport.personal_status.stars[0] }}
                </div>
                <div
                  v-if="dayReport.personal_status.warnings.length > 0"
                  class="text-[11px] text-error font-medium max-w-[220px] truncate"
                >
                  {{ dayReport.personal_status.warnings[0] }}
                </div>
              </td>
              <td class="py-3 px-3">
                <div
                  class="flex flex-wrap gap-1"
                  v-if="dayReport.yi_preview.length > 0"
                >
                  <span
                    class="px-1.5 py-0.5 bg-white border border-divider rounded text-[11px]"
                    v-for="(yiDoing, index) in dayReport.yi_preview.slice(0, 3)"
                    :key="index"
                  >
                    {{ yiDoing.name }} ({{ yiDoing.ua }})
                  </span>
                </div>
                <div class="text-error text-[11px]" v-else>
                  诸事不宜 ({{
                    $t("calculators.personal-date.month-table.routineOnly")
                  }})
                </div>
              </td>
              <td class="py-3 px-3 font-mono text-[11px] text-brown">
                {{ getBestHoursString(dayReport) }}
              </td>
              <td class="py-3 px-3 text-right">
                <span
                  class="px-3 py-1.5 rounded-xl bg-brown text-white text-xs font-semibold inline-flex items-center gap-1"
                >
                  <span>
                    {{
                      $t("calculators.personal-date.month-table.showDetailed")
                    }}
                  </span>
                  <span>→</span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
